import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { ErrorPage } from "@/pages/ErrorPage"
import type { FeatureFlagKey } from "@/types/featureFlag"
import type { ReactElement } from "react"

interface FeatureGateProps {
  /** Flag to gate on. */
  flag: FeatureFlagKey
  /**
   * Parent flag, for routes/sections that bypass their parent's own gate
   * (e.g. a layout Route) and so wouldn't otherwise inherit it.
   */
  parentFlag?: FeatureFlagKey
  children: ReactElement
}

/**
 * Route/section-level gate keyed by feature flag(s) — the flag counterpart
 * to PermissionGate. Unlike the bespoke per-route wrapper components in
 * src/App.tsx (ChatHistoryRoute, MaterialComplianceRoute, ...), this takes
 * the flag(s) as props so call sites with many flags don't each need a
 * dedicated wrapper component. Renders without `withLayout` — use it where
 * the app Layout is already applied by an ancestor.
 */
export function FeatureGate({ flag, parentFlag, children }: FeatureGateProps) {
  const enabled = useFeatureFlag(flag)
  // Always called (rules of hooks) — falls back to `flag` itself when there's
  // no parent, which is a no-op since the ternary below ignores it in that case.
  const parentEnabled = useFeatureFlag(parentFlag ?? flag)
  const isEnabled = parentFlag ? enabled && parentEnabled : enabled

  return isEnabled ? children : <ErrorPage variant="feature-unavailable" />
}
